import React from 'react';
import I18n from 'i18n-js';
import { graphql, ChildProps } from 'react-apollo';
import gql from 'graphql-tag';

import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import CategoryFlyoutHeader from '../../new_header/category_flyout_header';
import { useURL } from '@reverbdotcom/commons/src/url_context';
import { withUserContext, IUserContext } from '@reverbdotcom/commons/src/components/user_context_provider';
import { RCIcon } from '@reverbdotcom/commons/src/rc/RCIcon';
import StarEmptySvg from '@reverbdotcom/commons/design-system/icons/core/star-empty.svg?component';
import HeartEmptySvg from '@reverbdotcom/commons/design-system/icons/core/heart-empty.svg?component';
import ReverbLogoSvg from '@reverbdotcom/commons/design-system/icons/core/reverb-logo.svg?component';
import FeedAltSvg from '@reverbdotcom/commons/design-system/icons/core/feed-alt.svg?component';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';

import componentUrlHelpers from '../../shared/url_helpers';
import baseUrlHelpers from '../../../shared/url_helpers';

import SiteSearch from '../site_search/reverb_site_search';
import LogIn from './login';
import ReverbHeaderUserBadges from './reverb_header_user_badges';
import ReverbHeaderCartBadge, { ReverbHeaderCartBadgeFragment } from './reverb_header_cart_badge';
import ReverbHeaderDesktopMenu from './reverb_header_desktop_menu';
import ReverbHeaderMobileMenu from './reverb_header_mobile_menu';
import ReverbHeaderUserMenuFragment from './reverb_header_user_menu_fragment';
import { CoreReverbHeader } from '../../../schemas/rql';
import { isExperimentEnabled } from '@reverbdotcom/commons/src/user_context_helpers';
import experiments from '@reverbdotcom/commons/src/experiments';

interface IProps extends IUserContext {
  loginComponent?: React.ReactNode; // used to pass in an oauth login component from sandbox layout.tsx
}

export function ReverbHeader({ loginComponent, data, user }: ChildProps<IProps, CoreReverbHeader.Query>) {
  const COMPONENT_NAME = 'ReverbHeader';
  const [activeDropdownKey, setActiveDropdownKey] = React.useState();
  const { loggedOut } = user;
  const me = data?.me;
  const watchHeartExpEnabled = isExperimentEnabled(user, experiments.WATCH_ICON_HEART);

  const { query } = useURL();

  return (
    <>
      <div className="reverb-header__main">
        <div className="reverb-header__inner">
          <nav aria-label={I18n.t('discovery.reverbHeader.navigationRole')}>
            <CoreLink
              to="/"
              className="reverb-header__logo"
              aria-label={I18n.t('discovery.reverbHeader.reverbLogo')}
            >
              <RCIcon svgComponent={ReverbLogoSvg} />
            </CoreLink>
            <div className="reverb-header__nav reverb-header__nav--main">
              <div className="reverb-header__nav__search">
                <SiteSearch defaultValue={query.query} />
              </div>
            </div>
            <div className="reverb-header__nav reverb-header__nav--account reverb-header__nav--account-center">
              <ul className="reverb-header__nav__links">
                <li className="reverb-header__show-on-mobile">
                  <div className="mobile-d-none">
                    <CoreLink
                      className="reverb-header__nav__link reverb-header__nav__link--sell-button"
                      to={componentUrlHelpers.sellFormSearchPath({})}
                      clickEvent={{
                        componentName: COMPONENT_NAME,
                        entityId: componentUrlHelpers.sellFormSearchPath({}),
                        entityType: 'url',
                        eventName: MParticleEventName.ClickedComponent,
                      }}
                    >
                      {I18n.t('discovery.reverbHeader.sellCTAFull')}
                    </CoreLink>
                  </div>
                  <div className="d-none mobile-d-block">
                    <CoreLink
                      className="reverb-header__nav__link reverb-header__nav__link--sell-button"
                      to={componentUrlHelpers.sellFormSearchPath({})}
                      clickEvent={{
                        componentName: COMPONENT_NAME,
                        entityId: componentUrlHelpers.sellFormSearchPath({}),
                        entityType: 'url',
                        eventName: MParticleEventName.ClickedComponent,
                      }}
                    >
                      {I18n.t('discovery.reverbHeader.sellCTAMobile')}
                    </CoreLink>
                  </div>
                </li>
                <li>
                  <CoreLink
                    className="reverb-header__nav__link"
                    to={baseUrlHelpers.watchedProductsPath}
                  >
                    <div className="reverb-header__nav__link__icon">
                      <RCIcon svgComponent={watchHeartExpEnabled ? HeartEmptySvg : StarEmptySvg} />
                    </div>
                    <div className="reverb-header__nav__link__label">
                      {I18n.t('discovery.reverbHeader.watchList')}
                    </div>
                  </CoreLink>
                </li>
                <li>
                  <CoreLink
                    className="reverb-header__nav__link"
                    to={baseUrlHelpers.myFeedPath}
                  >
                    <div className="reverb-header__nav__link__icon">
                      <RCIcon svgComponent={FeedAltSvg} />
                    </div>
                    <div className="reverb-header__nav__link__label">
                      {I18n.t('discovery.reverbHeader.myFeed')}
                    </div>
                  </CoreLink>
                </li>
                <li className="reverb-header__show-on-mobile reverb-header__nav__link__auto-width">
                  <ReverbHeaderCartBadge
                    activeDropdownKey={activeDropdownKey}
                    setActiveDropdownKey={setActiveDropdownKey}
                    me={me}
                  />
                </li>
                {!loggedOut && (
                  <li className="reverb-header__show-on-mobile reverb-header__nav__link__auto-width">
                    <ReverbHeaderUserBadges
                      activeDropdownKey={activeDropdownKey}
                      setActiveDropdownKey={setActiveDropdownKey}
                    />
                  </li>
                )}
                {loggedOut && !loginComponent && <LogIn />}
                {loggedOut && loginComponent}
                {!loggedOut && (
                  <li className="reverb-header__nav__link__auto-width">
                    <ReverbHeaderDesktopMenu
                      activeDropdownKey={activeDropdownKey}
                      setActiveDropdownKey={setActiveDropdownKey}
                      me={me}
                    />
                  </li>
                )}
                <ReverbHeaderMobileMenu me={me} />
              </ul>
            </div>
          </nav>
        </div>
      </div>
      <div className="reverb-header__links">
        <CategoryFlyoutHeader />
      </div>
    </>
  );
}

const withQuery = graphql<IProps, CoreReverbHeader.Query>(
  gql`
    query Core_ReverbHeader {
      me {
        uuid
        ...ReverbHeaderCartBadgeData
        ...ReverbHeaderUserMenuData
      }
    }
    ${ReverbHeaderCartBadgeFragment}
    ${ReverbHeaderUserMenuFragment}
  `,
  {
    skip: (ownProps) => (ownProps.user.loggedOut),
    options: {
      ssr: false,
    },
  },
);

export default withUserContext(withQuery(ReverbHeader));
